import { useCallback, useEffect, useState } from 'react';

// `consents` is France-only — DashboardPage filters the tab list per region.
// Keep it in the master list so URL params (`?tab=consents`) still hydrate
// when someone bookmarks a France link.
export const TAB_KEYS = ['overview', 'engagement', 'games', 'rewards', 'consents'] as const;
export type TabKey = (typeof TAB_KEYS)[number];

function isTabKey(v: string | null): v is TabKey {
  return v !== null && (TAB_KEYS as readonly string[]).includes(v);
}

function readTab(available: ReadonlyArray<TabKey>): TabKey {
  const value = new URLSearchParams(window.location.search).get('tab');
  if (isTabKey(value) && available.includes(value)) return value;
  // Fall back to the first available tab — never strand the user on a tab
  // that doesn't exist for this region (e.g. ?tab=consents on Brazil).
  return available[0] ?? 'overview';
}

export function useTabParam(available: ReadonlyArray<TabKey> = TAB_KEYS): [TabKey, (next: TabKey) => void] {
  const [tab, setTab] = useState<TabKey>(() => readTab(available));

  useEffect(() => {
    const onPop = () => setTab(readTab(available));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [available]);

  const set = useCallback((next: TabKey) => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.pushState(null, '', url);
    setTab(next);
  }, []);

  return [tab, set];
}
